import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, useMotionValue, useSpring } from 'framer-motion';
import { ArrowLeft, ArrowRight, CheckCircle, Star, Shield, Gem, Users, Award, Clock } from 'lucide-react';

/* ── Animated counter ─────────────────────────────────────── */
const Counter = ({ target, suffix = '', prefix = '' }) => {
    const ref = useRef(null);
    const motionVal = useMotionValue(0);
    const spring = useSpring(motionVal, { stiffness: 60, damping: 20 });
    const inView = useInView(ref, { once: true, margin: '-60px' });
    const [display, setDisplay] = React.useState(0);

    useEffect(() => {
        if (inView) motionVal.set(target);
    }, [inView, target, motionVal]);

    useEffect(() => {
        const unsub = spring.on('change', (v) => setDisplay(Math.round(v)));
        return unsub;
    }, [spring]);

    return (
        <span ref={ref}>
            {prefix}{display}{suffix}
        </span>
    );
};

/* ── Timeline item ─────────────────────────────────────────── */
const TimelineItem = ({ year, title, description, isRight, index }) => (
    <motion.div
        initial={{ opacity: 0, x: isRight ? 40 : -40 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: index * 0.1 }}
        className={`relative flex items-start gap-8 ${isRight ? 'flex-row-reverse text-right' : ''} mb-12`}
    >
        {/* content */}
        <div className="flex-1 max-w-sm">
            <span
                className="inline-block text-xs uppercase tracking-widest font-semibold mb-2"
                style={{ color: '#D4AF37' }}
            >
                {year}
            </span>
            <h4 className="font-serif text-xl text-white mb-2">{title}</h4>
            <p className="text-gray-500 text-sm leading-relaxed">{description}</p>
        </div>

        {/* dot */}
        <div className="flex-shrink-0 flex flex-col items-center">
            <div
                className="w-5 h-5 rounded-full border-4 border-black z-10"
                style={{ background: '#D4AF37' }}
            />
        </div>

        {/* spacer for opposite side */}
        <div className="flex-1 max-w-sm" />
    </motion.div>
);

/* ── Value card ─────────────────────────────────────────────── */
const ValueCard = ({ icon, title, desc, delay }) => (
    <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay }}
        className="flex flex-col p-7 rounded-sm border border-gray-800 hover:border-yellow-600/50 transition-all duration-300 group"
        style={{ background: '#161616' }}
    >
        <div
            className="w-12 h-12 rounded-full flex items-center justify-center mb-5"
            style={{ background: 'rgba(212,175,55,0.1)', color: '#D4AF37' }}
        >
            {icon}
        </div>
        <h3 className="font-serif text-lg text-white mb-2 group-hover:text-yellow-400 transition-colors">{title}</h3>
        <p className="text-gray-500 text-sm leading-relaxed">{desc}</p>
    </motion.div>
);

/* ── Main About Page ─────────────────────────────────────────── */
const AboutPage = () => {
    const YEAR_FOUNDED = 2014;
    const yearsInBusiness = new Date().getFullYear() - YEAR_FOUNDED;

    useEffect(() => { window.scrollTo(0, 0); }, []);

    return (
        <div className="font-sans bg-[#0e0e0e] min-h-screen text-white">

            {/* ── Sticky nav ── */}
            <div
                className="fixed top-0 left-0 w-full z-50"
                style={{ background: 'rgba(14,14,14,0.9)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(212,175,55,0.1)' }}
            >
                {/* Desktop: single space-between row */}
                <div className="hidden md:flex items-center justify-between px-8 py-4">
                    <Link to="/" className="flex items-center gap-2 text-white/70 hover:text-white transition-colors text-xs uppercase tracking-widest">
                        <ArrowLeft size={15} /> Home
                    </Link>
                    <Link to="/" className="flex items-center gap-2 leading-none">
                        <span className="font-serif text-base text-white tracking-widest">ROYAL</span>
                        <img src="/logo.png" alt="Royal Standard Logo" style={{ height: '28px', width: 'auto', objectFit: 'contain', display: 'block' }} />
                        <span className="font-serif text-base tracking-widest" style={{ color: '#D4AF37' }}>STANDARD</span>
                    </Link>
                    <Link to="/contact" className="text-xs uppercase tracking-widest px-5 py-2.5 font-semibold text-black hover:opacity-90 transition-opacity" style={{ background: '#D4AF37' }}>
                        Get a Quote
                    </Link>
                </div>

                {/* Mobile: compact logo row + full-width CTA */}
                <div className="md:hidden">
                    <div className="flex items-center justify-between px-4 py-3">
                        <Link to="/" className="flex items-center gap-1.5 text-white/70 hover:text-white transition-colors text-xs uppercase tracking-wider">
                            <ArrowLeft size={14} />
                            <span className="hidden xs:inline">Home</span>
                        </Link>
                        <Link to="/" className="flex items-center gap-1.5 leading-none">
                            <span className="font-serif text-sm text-white tracking-widest">ROYAL</span>
                            <img src="/logo.png" alt="Royal Standard Logo" style={{ height: '22px', width: 'auto', objectFit: 'contain', display: 'block' }} />
                            <span className="font-serif text-sm tracking-widest" style={{ color: '#D4AF37' }}>STANDARD</span>
                        </Link>
                        <div className="w-8" />
                    </div>
                    <Link
                        to="/contact"
                        className="flex items-center justify-center gap-2 w-full py-2.5 text-black text-xs uppercase tracking-widest font-semibold"
                        style={{ background: '#D4AF37' }}
                    >
                        Get a Free Quote <ArrowRight size={12} />
                    </Link>
                </div>
            </div>

            {/* ── Hero ── */}
            <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
                {/* background */}
                <div className="absolute inset-0">
                    <img
                        src="/Joinery/9.jpeg"
                        alt="Royal Standard craftsmanship"
                        className="w-full h-full object-cover opacity-15"
                    />
                    <div className="absolute inset-0" style={{
                        background: 'radial-gradient(ellipse at center, rgba(212,175,55,0.06) 0%, #0e0e0e 70%)'
                    }} />
                </div>

                <div className="relative z-10 text-center px-6 max-w-4xl pt-20">
                    <motion.p
                        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                        className="uppercase tracking-[0.4em] text-xs font-semibold mb-6"
                        style={{ color: '#D4AF37' }}
                    >
                        Established {YEAR_FOUNDED} · Dubai, UAE
                    </motion.p>
                    <motion.h1
                        initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
                        className="font-serif text-6xl md:text-8xl text-white mb-8 leading-[1.05]"
                    >
                        Crafting <em className="not-italic" style={{ color: '#D4AF37' }}>Excellence</em><br />Since {YEAR_FOUNDED}
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
                        className="text-gray-400 text-lg md:text-xl font-light max-w-2xl mx-auto mb-12 leading-relaxed"
                    >
                        For over {yearsInBusiness} years, Royal Standard has been the UAE's trusted name in premium marble polishing, tile installation, ceiling works and bespoke joinery — transforming spaces into lasting masterpieces.
                    </motion.p>

                    {/* Stats row */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }}
                        className="grid grid-cols-2 md:grid-cols-4 gap-px border border-gray-800 rounded-sm overflow-hidden"
                    >
                        {[
                            { val: yearsInBusiness, suffix: '+', label: 'Years of Excellence' },
                            { val: 1200, suffix: '+', label: 'Projects Completed' },
                            { val: 850, suffix: '+', label: 'Happy Clients' },
                            { val: 4, suffix: '', label: 'Core Services' },
                        ].map((s, i) => (
                            <div key={i} className="flex flex-col items-center py-8 px-4" style={{ background: '#161616' }}>
                                <span className="font-serif text-3xl md:text-4xl font-bold mb-1" style={{ color: '#D4AF37' }}>
                                    <Counter target={s.val} suffix={s.suffix} />
                                </span>
                                <span className="text-gray-500 text-xs uppercase tracking-wider text-center">{s.label}</span>
                            </div>
                        ))}
                    </motion.div>
                </div>

                {/* scroll cue */}
                <motion.div
                    className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 text-white/30"
                    animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}
                >
                    <span className="text-xs uppercase tracking-widest">Our Story</span>
                    <ArrowRight size={16} className="rotate-90" />
                </motion.div>
            </section>

            {/* ── Our Story ── */}
            <section className="py-24 border-t border-white/5">
                <div className="max-w-6xl mx-auto px-6 flex flex-col lg:flex-row gap-16 items-center">
                    {/* image collage */}
                    <motion.div
                        initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
                        className="lg:w-1/2 grid grid-cols-2 gap-3"
                    >
                        <div className="col-span-2 h-64 overflow-hidden rounded-sm">
                            <img src="/Joinery/9.jpeg" alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                        </div>
                        <div className="h-40 overflow-hidden rounded-sm">
                            <img src="Ceiling/13.jpg" alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                        </div>
                        <div className="h-40 overflow-hidden rounded-sm">
                            <img src="/Joinery/7.jpeg" alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                        </div>
                    </motion.div>

                    {/* text */}
                    <motion.div
                        initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
                        className="lg:w-1/2"
                    >
                        <p className="uppercase tracking-widest text-xs font-semibold mb-3" style={{ color: '#D4AF37' }}>Our Story</p>
                        <h2 className="font-serif text-4xl text-white mb-6 leading-tight">
                            From a Craftsman's Vision to the UAE's Premier Finishing Experts
                        </h2>
                        <div className="w-14 h-px mb-6" style={{ background: '#D4AF37' }} />
                        <div className="space-y-4 text-gray-400 text-base leading-relaxed">
                            <p>
                                Royal Standard was founded in 2014 in Dubai by a team of master craftsmen who shared a single conviction: that every surface in a home deserves to be treated with the same precision and care as a work of art.
                            </p>
                            <p>
                                What began as a specialist marble polishing workshop quickly earned a reputation for uncompromising quality across Dubai's most prestigious developments. Word spread through villa communities, hotel operators and interior designers who demanded nothing less than perfection.
                            </p>
                            <p>
                                Today, over {yearsInBusiness} years later, Royal Standard operates across all seven Emirates — offering marble restoration, tile and marble installation, bespoke ceiling works and custom joinery from a single, trusted source.
                            </p>
                        </div>
                        <Link
                            to="/contact"
                            className="inline-flex items-center gap-2 mt-8 text-sm uppercase tracking-widest px-7 py-3 font-semibold text-black"
                            style={{ background: '#D4AF37' }}
                        >
                            Work With Us <ArrowRight size={14} />
                        </Link>
                    </motion.div>
                </div>
            </section>

            {/* ── Timeline ── */}
            <section className="py-24 border-t border-white/5">
                <div className="max-w-4xl mx-auto px-6">
                    <div className="text-center mb-16">
                        <p className="uppercase tracking-widest text-xs font-semibold mb-3" style={{ color: '#D4AF37' }}>Our Journey</p>
                        <h2 className="font-serif text-4xl text-white">A Decade of Growth</h2>
                        <div className="w-14 h-px mx-auto mt-5" style={{ background: '#D4AF37' }} />
                    </div>

                    {/* vertical line */}
                    <div className="relative">
                        <div
                            className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-px"
                            style={{ background: 'rgba(212,175,55,0.2)' }}
                        />
                        {[
                            { year: '2014', title: 'Founded in Dubai', description: 'Royal Standard opens its first workshop in Al Quoz, Dubai, specialising exclusively in marble restoration for luxury residential clients.' },
                            { year: '2016', title: 'Tile Installation Launch', description: 'Growing demand from clients leads us to expand into precision tile and marble installation for floors, walls and bathroom feature spaces.' },
                            { year: '2018', title: 'Ceiling Works Division', description: 'A dedicated ceiling works team is established, delivering bespoke gypsum, cove lighting and coffered ceiling systems across Dubai villas.' },
                            { year: '2020', title: 'Joinery & Carpentry', description: 'Our bespoke joinery workshop launches, offering fully custom wardrobes, cabinetry and architectural woodwork crafted to millimetre precision.' },
                            { year: '2022', title: 'UAE-Wide Operations', description: 'Royal Standard expands its service area to cover all seven Emirates, completing projects in Abu Dhabi, Sharjah, Ajman and beyond.' },
                            { year: '2024', title: '10 Years · 1,200+ Projects', description: 'We celebrate a decade of excellence — over 1,200 completed projects, 850+ satisfied clients and a growing team of 60 skilled craftsmen.' },
                        ].map((item, i) => (
                            <TimelineItem key={i} index={i} isRight={i % 2 !== 0} {...item} />
                        ))}
                    </div>
                </div>
            </section>

            {/* ── Values ── */}
            <section className="py-24 border-t border-white/5">
                <div className="max-w-6xl mx-auto px-6">
                    <div className="text-center mb-14">
                        <p className="uppercase tracking-widest text-xs font-semibold mb-3" style={{ color: '#D4AF37' }}>What Drives Us</p>
                        <h2 className="font-serif text-4xl text-white">Our Core Values</h2>
                        <div className="w-14 h-px mx-auto mt-5" style={{ background: '#D4AF37' }} />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {[
                            { icon: <Gem size={22} />, title: 'Uncompromising Quality', desc: 'We use only grade-A materials and proven techniques. Every surface we touch must meet our exacting internal standard before we leave.' },
                            { icon: <Shield size={22} />, title: 'Integrity & Transparency', desc: 'Our quotes are fixed — no hidden costs, no surprises. We say what we will do, and we do what we say.' },
                            { icon: <Users size={22} />, title: 'Client-First Approach', desc: 'Your timeline, lifestyle and vision guide every decision we make. We listen first, then deliver.' },
                            { icon: <Star size={22} />, title: 'Mastery & Craft', desc: 'Our craftsmen undergo continuous training and hold international certifications in stone care and interior finishing.' },
                            { icon: <Award size={22} />, title: 'Pride in Every Project', desc: 'From a modest apartment bathroom to a palace hallway, every project receives the same level of dedication and attention.' },
                            { icon: <Clock size={22} />, title: 'On Time, Every Time', desc: 'We respect your time and schedule commitments. Our project management ensures timely delivery without compromising quality.' },
                        ].map((v, i) => (
                            <ValueCard key={i} delay={i * 0.07} {...v} />
                        ))}
                    </div>
                </div>
            </section>

            {/* ── Why Choose Royal Standard ── */}
            <section className="py-24 border-t border-white/5">
                <div className="max-w-6xl mx-auto px-6 flex flex-col lg:flex-row gap-16 items-center">
                    <motion.div
                        initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
                        className="lg:w-1/2"
                    >
                        <p className="uppercase tracking-widest text-xs font-semibold mb-3" style={{ color: '#D4AF37' }}>Why Royal Standard</p>
                        <h2 className="font-serif text-4xl text-white mb-6 leading-tight">The Standard Your Space Deserves</h2>
                        <div className="w-14 h-px mb-8" style={{ background: '#D4AF37' }} />
                        <div className="space-y-4">
                            {[
                                'Over 11 years of proven expertise across the UAE',
                                'Master craftsmen certified in stone care & interior finishing',
                                'Full-service — from design consultation to completion',
                                'Insured work with written quality guarantee',
                                'Available across all 7 Emirates',
                                'Arabic & English speaking project managers',
                            ].map((item, i) => (
                                <motion.div
                                    key={i}
                                    initial={{ opacity: 0, x: -20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: i * 0.07 }}
                                    className="flex items-start gap-3"
                                >
                                    <CheckCircle size={18} className="flex-shrink-0 mt-0.5" style={{ color: '#D4AF37' }} />
                                    <span className="text-gray-400 text-sm leading-relaxed">{item}</span>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>

                    {/* image */}
                    <motion.div
                        initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
                        className="lg:w-1/2"
                    >
                        <div className="relative rounded-sm overflow-hidden h-[480px]">
                            <img
                                src="/Marble polish/m4.jpg"
                                alt="Craftsman at work"
                                className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                            {/* badge */}
                            <div
                                className="absolute bottom-8 left-8 px-6 py-5 rounded-sm"
                                style={{ background: 'rgba(14,14,14,0.88)', border: '1px solid rgba(212,175,55,0.3)', backdropFilter: 'blur(8px)' }}
                            >
                                <p className="text-white font-serif text-2xl font-bold" style={{ color: '#D4AF37' }}>
                                    Est. {YEAR_FOUNDED}
                                </p>
                                <p className="text-gray-400 text-xs uppercase tracking-widest mt-1">Dubai · UAE</p>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* ── CTA ── */}
            <section className="py-20 border-t border-white/5 text-center">
                <div className="max-w-2xl mx-auto px-6">
                    <motion.p
                        initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                        className="uppercase tracking-widest text-xs font-semibold mb-4"
                        style={{ color: '#D4AF37' }}
                    >
                        Ready to Begin?
                    </motion.p>
                    <motion.h2
                        initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}
                        className="font-serif text-4xl text-white mb-6 leading-tight"
                    >
                        Let Royal Standard Elevate Your Space
                    </motion.h2>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}
                        className="flex flex-col sm:flex-row gap-4 justify-center"
                    >
                        <Link
                            to="/contact"
                            className="inline-flex items-center gap-2 px-8 py-4 text-sm uppercase tracking-widest font-semibold text-black"
                            style={{ background: '#D4AF37' }}
                        >
                            Contact Us <ArrowRight size={16} />
                        </Link>
                        <Link
                            to="/#services"
                            className="inline-flex items-center gap-2 px-8 py-4 text-sm uppercase tracking-widest font-semibold text-white border border-gray-700 hover:border-yellow-500 transition-colors"
                        >
                            Our Services
                        </Link>
                    </motion.div>
                </div>
            </section>




        </div>
    );
};

export default AboutPage;
